import { t } from "@lingui/macro";
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { HeaderFontFamily } from "@whoosh/design-system/src";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { LocaleTransitionBoundary } from "../../features/LanguagePreference";
import { TabBarNav } from "../../features/Navigation";
import { EventsScreen, HomeScreen } from "../../screens";
import { WebViewScreen } from "../../screens/WebViewScreen";
import { useFlags } from "../../utils/launchDarkly/useFlags";
import { ProfileStackNavigator } from "../ProfileStackNavigator";
import { HomeTabsNavigator_session$key } from "./__generated__/HomeTabsNavigator_session.graphql";
import { HomeTabsNavigatorParamList } from "./types";

const Tab = createBottomTabNavigator<HomeTabsNavigatorParamList>();

type Props = {
  sessionRef: HomeTabsNavigator_session$key;
};

export const HomeTabsNavigator = ({ sessionRef }: Props) => {
  const session = useFragment(
    HomeTabsNavigatorSessionFragmentString,
    sessionRef
  );

  const hasEvents =
    !!session?.currentClientProfile?.member?.club.departments?.edges?.some(
      (e) => e?.node?.anyFacilityEventsEnabled
    );

  const { memAppRosterVisibility, newTopbar } = useFlags();

  const shouldShowMenuHamburger = memAppRosterVisibility && hasEvents;

  return (
    <Box flex={1}>
      <Tab.Navigator
        screenLayout={({ children }) => (
          <LocaleTransitionBoundary>{children}</LocaleTransitionBoundary>
        )}
        tabBar={(props) => {
          const currentRoute = props.state.routes[props.state.index]?.name;
          if (newTopbar) {
            return currentRoute === "MainHome" ? (
              <TabBarNav navigation={props.navigation} sessionRef={session} />
            ) : null;
          } else {
            return currentRoute === "WebView" ? null : (
              <TabBarNav navigation={props.navigation} sessionRef={session} />
            );
          }
        }}
        screenOptions={() => ({
          headerShadowVisible: true,
          headerBackTitleVisible: false,
          headerTitleAlign: "center",
          headerTitleStyle: {
            fontFamily: HeaderFontFamily,
            fontSize: 20,
            color: "main.primary.1000",
          },
          popToTopOnBlur: true,
          sceneStyle: {
            position: "relative",
          },
        })}
      >
        <Tab.Screen
          name="MainHome"
          options={{
            headerShown: false,
            tabBarLabel: "Home",
            title: t`Home`,
          }}
        >
          {() => <HomeScreen sessionRef={session} />}
        </Tab.Screen>
        {hasEvents ? (
          <Tab.Screen
            name="Events"
            component={EventsScreen}
            options={{
              headerShown: false,
              title: t`Events`,
            }}
          />
        ) : null}

        <Tab.Screen
          name="Profile"
          component={ProfileStackNavigator}
          options={{
            headerShown: false,
            tabBarItemStyle: {
              display: shouldShowMenuHamburger ? "none" : "flex",
            },
          }}
        />
        <Tab.Screen
          name="WebView"
          component={WebViewScreen}
          options={{
            headerShown: false,
            tabBarStyle: { display: "none" },
          }}
        />
      </Tab.Navigator>
    </Box>
  );
};

const HomeTabsNavigatorSessionFragmentString = graphql`
  fragment HomeTabsNavigator_session on Session {
    ...TabBarNav_session
    ...HomeScreen_session
    currentClientProfile {
      member {
        club {
          departments {
            edges {
              node {
                anyFacilityEventsEnabled
              }
            }
          }
        }
      }
    }
  }
`;
